import React, {useEffect, useState} from 'react';
import {getLocaleContent} from '@helper/locale-content.js';
import DepartmentService from '../../services/department.service';
import {useRouter} from 'next/router';
import LinkSpecialtyDetail from '@components/common/Link/LinkSpecialtyDetail';
import LoadingNavigation from '@components/common/Loading/LoadingNavigation';

export default function Specialties({medicalFacility}) {
    const {locale} = useRouter();
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const gridRef = React.useRef(null);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const facility = Array.isArray(medicalFacility) ? medicalFacility[0] : medicalFacility;

        if (!facility?.business_id) {
            setLoading(false);
            return;
        }

        const departmentService = new DepartmentService();
        departmentService
            .getDepartmentsByBusiness({
                business_id: facility.business_id,
                locale: locale,
            })
            .then(response => {
                if (Array.isArray(response)) {
                    setDepartments(response);
                } else if (response?.data && Array.isArray(response.data)) {
                    setDepartments(response.data);
                } else {
                    setDepartments([]);
                }
            })
            .catch(error => {
                console.error('Lỗi khi lấy danh sách chuyên khoa:', error);
                setDepartments([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [medicalFacility?.business_id, locale]);

    const handleScroll = () => {
        const el = gridRef.current;
        if (!el) return;
        const scrollLeft = el.scrollLeft;
        const maxScroll = el.scrollWidth - el.clientWidth;
        const percent = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
        setProgress(percent);
    };

    if (loading) {
        return (
            <section className="specialties">
                <h2 className="specialties-title">
                    {getLocaleContent(locale, 'page.medical-facility.specialties-of-hospital')}
                </h2>
                <div
                    className="specialties-grid"
                    style={{
                        minHeight: '200px',
                    }}>
                    <LoadingNavigation size="large" color="#006940" />
                </div>
            </section>
        );
    }

    if (!departments?.length) return null;

    // Tính danh sách hiển thị (2 dòng = 10 phần tử nếu mỗi dòng 5 item)
    const visibleDepartments = !isMobile ? (showAll ? departments : departments.slice(0, 10)) : departments;

    const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size));
        }
        return chunks;
    };

    const renderDepartments = () => {
        if (isMobile) {
            const chunks = chunkArray(visibleDepartments, 4);
            return chunks.map((group, index) => (
                <div key={index} className="specialties-grid-page">
                    {group.map(department => (
                        <LinkSpecialtyDetail key={department.department_id} department={department}>
                            <div className="specialty-card">
                                {department.image_url ? (
                                    <img
                                        src={department.image_url}
                                        alt={department.department_name}
                                        className="specialty-image"
                                        onError={e => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : (
                                    <div className="specialty-placeholder">
                                        {getLocaleContent(locale, 'page.medical-facility.no-image')}
                                    </div>
                                )}
                                <p className="specialty-name">{department.department_name}</p>
                            </div>
                        </LinkSpecialtyDetail>
                    ))}
                </div>
            ));
        }

        return visibleDepartments.map(department => (
            <LinkSpecialtyDetail key={department.department_id} department={department}>
                <div className="specialty-card">
                    {department.image_url ? (
                        <img
                            src={department.image_url}
                            alt={department.department_name}
                            className="specialty-image"
                            onError={e => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : (
                        <div className="specialty-placeholder">
                            {getLocaleContent(locale, 'page.medical-facility.no-image')}
                        </div>
                    )}
                    <p className="specialty-name">{department.department_name}</p>
                </div>
            </LinkSpecialtyDetail>
        ));
    };

    return (
        <section className="specialties">
            <h2 className="specialties-title">
                {getLocaleContent(locale, 'page.medical-facility.specialties-of-hospital')}
            </h2>
            <div className="specialties-grid-wrapper">
                <div
                    className={`specialties-grid ${
                        showAll ? 'specialties-grid--expanded' : 'specialties-grid--collapsed'
                    }`}
                    ref={gridRef}
                    onScroll={handleScroll}>
                    {renderDepartments()}
                </div>

                {!isMobile && departments.length > 10 && (
                    <div className="specialties-loadmore-wrapper">
                        <button className="load-more" onClick={() => setShowAll(!showAll)}>
                            {showAll
                                ? getLocaleContent(locale, 'page.medical-facility.show-less') || 'Thu gọn'
                                : getLocaleContent(locale, 'page.medical-facility.show-more') || 'Xem thêm'}
                            <img src="/bw_image/chevron-down-double.svg"></img>
                        </button>
                    </div>
                )}

                {isMobile && (
                    <div className="specialties-progressbar">
                        <div className="specialties-progressbar-fill" style={{width: `${progress}%`}}></div>
                    </div>
                )}
            </div>
        </section>
    );
}
